import { useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Card,
  Center,
  Code,
  Divider,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { unwrap } from '../api/client';
import { createOrganization, me } from '../api/gen';
import { AUTH_MODE, AuthError, forgotPassword, login, resendVerification } from '../lib/auth';
import { useSession } from '../lib/session';

// Signing in, in the two shapes this app has, and then choosing a workspace.
//
// Under the product build it is an address and a password against the app's
// own accounts, with the two ways out a person actually needs — the address
// was never confirmed, or the password is gone — offered where the failure
// happens. Under the header seam — local development and the tests — the
// subject is typed, which is exactly as unsafe as it sounds and is why it is a
// build-time setting the production bundle cannot be talked into.
export function SignInPage() {
  const { signedIn, setOrgId } = useSession();
  if (!signedIn) return <SignIn />;
  return <ChooseWorkspace onChosen={setOrgId} />;
}

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [sent, setSent] = useState<'verification' | 'reset' | null>(null);
  const [busy, setBusy] = useState(false);
  const header = AUTH_MODE === 'header';
  const unconfirmed = error instanceof AuthError && error.status === 403;

  async function run(what: () => Promise<void>, then: 'verification' | 'reset' | null = null) {
    setBusy(true);
    setError(null);
    try {
      await what();
      setSent(then);
    } catch (e) {
      setError(e as Error);
      setSent(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard title="Inbox" subtitle="Every channel your customers write on, in one place.">
      {header ? (
        <>
          <Text size="sm" c="dimmed">
            This build uses the header seam. Name the subject the server should treat you as.
          </Text>
          <TextInput
            label="Subject"
            placeholder="user-acme-owner"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
        </>
      ) : (
        <>
          <TextInput
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <PasswordInput
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void run(() => login(email, password));
            }}
          />
        </>
      )}

      {error ? (
        <Alert color="red" variant="light">
          {error.message}
        </Alert>
      ) : null}
      {sent === 'verification' ? (
        <Text size="sm">The confirmation link is on its way. Look in your inbox.</Text>
      ) : null}
      {sent === 'reset' ? (
        <Text size="sm">If this address has an account, a link is on its way to it.</Text>
      ) : null}

      <Button loading={busy} disabled={!email.trim()} onClick={() => void run(() => login(email, password))}>
        {header ? 'Continue' : 'Sign in'}
      </Button>

      {/* The way out of an unconfirmed address, offered where it fails. */}
      {!header && unconfirmed ? (
        <Button
          variant="light"
          loading={busy}
          onClick={() => void run(() => resendVerification(email), 'verification')}
        >
          Send the confirmation link again
        </Button>
      ) : null}

      {!header ? (
        <Group justify="space-between">
          <Anchor component={Link} to="/register" size="sm">
            Create an account
          </Anchor>
          <Anchor
            component="button"
            type="button"
            size="sm"
            disabled={!email.trim()}
            onClick={() => void run(() => forgotPassword(email), 'reset')}
          >
            Forgot your password?
          </Anchor>
        </Group>
      ) : null}
    </AuthCard>
  );
}

// A signed-in account in no workspace is a real state — a fresh registration
// is in it — so it gets the two ways out: found one, or be seated in one by
// its owner, who needs this account's id to do it.
function ChooseWorkspace({ onChosen }: { onChosen: (id: string) => void }) {
  const { signOut } = useSession();
  const { data, isLoading, error } = useQuery({ queryKey: ['me'], queryFn: () => unwrap(me()) });
  const memberships = data?.memberships ?? [];

  return (
    <AuthCard title="Choose a workspace">
      {isLoading ? <Text size="sm">Loading…</Text> : null}
      {error ? (
        <Text size="sm" c="red">
          {(error as Error).message}
        </Text>
      ) : null}
      {memberships.map((m) => (
        <Button key={m.orgId} variant="light" onClick={() => onChosen(m.orgId)}>
          {m.orgName} · {m.role}
        </Button>
      ))}
      {!isLoading && data && memberships.length === 0 ? (
        <Text size="sm" c="dimmed">
          This account is not a member of any workspace yet. Found one below, or give a
          workspace owner your account id to be seated: <Code>{data.subject}</Code>
        </Text>
      ) : null}
      {data ? (
        <>
          <Divider label="or found a new one" labelPosition="center" />
          <FoundWorkspace onFounded={onChosen} />
        </>
      ) : null}
      <Anchor component="button" type="button" size="sm" onClick={signOut}>
        Sign out
      </Anchor>
    </AuthCard>
  );
}

function FoundWorkspace({ onFounded }: { onFounded: (id: string) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const found = useMutation({
    mutationFn: () =>
      unwrap(createOrganization({ body: { name: name.trim(), slug: slug.trim(), displayName: displayName.trim() } })),
    onSuccess: (out) => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      onFounded(out.organization.id);
    },
  });

  return (
    <>
      <TextInput
        label="Workspace name"
        value={name}
        onChange={(e) => {
          const next = e.currentTarget.value;
          setName(next);
          setSlug(
            next
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, ''),
          );
        }}
      />
      <TextInput
        label="Short name"
        description="Lower-case letters, digits and hyphens."
        value={slug}
        onChange={(e) => setSlug(e.currentTarget.value)}
      />
      <TextInput
        label="Your name in it"
        value={displayName}
        onChange={(e) => setDisplayName(e.currentTarget.value)}
      />
      {found.error ? (
        <Text size="sm" c="red">
          {(found.error as Error).message}
        </Text>
      ) : null}
      <Button
        variant="default"
        loading={found.isPending}
        disabled={!name.trim() || slug.trim().length < 2 || !displayName.trim()}
        onClick={() => found.mutate()}
      >
        Found the workspace
      </Button>
    </>
  );
}

/** The card every signed-out screen is drawn in. */
export function AuthCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Center mih="100dvh" p="md">
      <Card withBorder padding="lg" radius="md" maw={420} w="100%">
        <Stack>
          <div>
            <Title order={4}>{title}</Title>
            {subtitle ? (
              <Text size="sm" c="dimmed">
                {subtitle}
              </Text>
            ) : null}
          </div>
          {children}
        </Stack>
      </Card>
    </Center>
  );
}
